import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { DataStoreService } from 'src/app/order/store/data-store.service';
import { TokenModel } from 'src/app/shared/models/response/token.model';
import { environment } from 'src/environments/environment.local';
import { RoutingService } from '../routing/routing.service';
import { TokenService } from '../token/token.service';

export const SESSION_REFRESH_ADVANCE_MS = 2 * 60 * 1000;
export const SESSION_REFRESH_RETRY_MS = 30 * 1000;
const MAX_TIMEOUT_MS = 2147483647;

@Injectable({ providedIn: 'root' })
export class SessionRefreshService {
  private readonly http: HttpClient;
  private timer: ReturnType<typeof setTimeout> = null;
  private request: Subscription = null;

  constructor(
    httpBackend: HttpBackend,
    private dataStoreService: DataStoreService,
    private tokenService: TokenService,
    private routingService: RoutingService
  ) {
    // HttpBackend ignora os interceptors: o refresh não envia x-online-accreditation e não aciona o loading que bloqueia a tela.
    this.http = new HttpClient(httpBackend);
  }

  // Agenda a renovação somente se o sessionToken mais recente do storage tiver a claim ga3Session.
  public start(): void {
    this.stop();

    const expiresAt = this.getRenewableExpiration(this.dataStoreService.getToken());
    if (expiresAt !== null) {
      this.schedule(expiresAt - SESSION_REFRESH_ADVANCE_MS - Date.now());
    }
  }

  public stop(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.request) {
      this.request.unsubscribe();
      this.request = null;
    }
  }

  private schedule(delayMs: number): void {
    this.timer = setTimeout(() => this.onTimer(), Math.min(Math.max(delayMs, 0), MAX_TIMEOUT_MS));
  }

  private onTimer(): void {
    this.timer = null;

    const token = this.dataStoreService.getToken();
    const expiresAt = this.getRenewableExpiration(token);
    if (expiresAt === null) {
      return;
    }

    // O token pode ter sido renovado por outra aba; nesse caso basta reagendar.
    const delayMs = expiresAt - SESSION_REFRESH_ADVANCE_MS - Date.now();
    if (delayMs > 0) {
      this.schedule(delayMs);
      return;
    }

    this.refresh(token);
  }

  private refresh(token: string): void {
    this.request = this.http
      .post<TokenModel>(`${environment.apiUrls.authentication}/refresh`, { token }, { headers: { 'Api-Key': `${environment.apiKey}` } })
      .subscribe({
        next: (response) => {
          this.request = null;
          this.handleSuccess(response, token);
        },
        error: (error: HttpErrorResponse) => {
          this.request = null;
          this.handleError(error, token);
        },
      });
  }

  private handleSuccess(response: TokenModel, sentToken: string): void {
    // 200 sem corpo: nada a renovar; a sessão segue até o próprio exp.
    if (!response) {
      return;
    }

    if (response.fgBloqueado !== undefined && response.fgBloqueado !== null && response.fgBloqueado !== 'N') {
      this.endSession();
      return;
    }

    if (!response.sessionToken || !this.tokenService.getDecodedAccessToken(response.sessionToken)) {
      return;
    }

    // Só substitui se o storage ainda tiver o token enviado (outra aba pode ter renovado, feito logout ou outro login).
    if (this.dataStoreService.getToken() === sentToken) {
      this.dataStoreService.setToken(response.sessionToken);
    }

    this.start();
  }

  private handleError(error: HttpErrorResponse, sentToken: string): void {
    const status = error ? error.status : 0;

    if (status === 401 || status === 403) {
      // Outra aba pode ter renovado ou logado novamente enquanto este refresh estava em andamento.
      if (this.dataStoreService.getToken() !== sentToken) {
        this.start();
        return;
      }
      this.endSession();
      return;
    }

    if (status === 0 || status >= 500) {
      const expiresAt = this.getRenewableExpiration(this.dataStoreService.getToken());
      if (expiresAt !== null && Date.now() + SESSION_REFRESH_RETRY_MS < expiresAt) {
        this.schedule(SESSION_REFRESH_RETRY_MS);
      }
    }
  }

  private endSession(): void {
    this.stop();
    this.dataStoreService.clearSessionInformation();
    this.routingService.navigateToLogin(true, false);
  }

  private getRenewableExpiration(token: string): number {
    if (!token) {
      return null;
    }

    const decoded = this.tokenService.getDecodedAccessToken(token);
    if (!decoded || decoded.ga3Session === undefined || decoded.ga3Session === null || typeof decoded.exp !== 'number') {
      return null;
    }

    const expiresAt = decoded.exp * 1000;
    return expiresAt > Date.now() ? expiresAt : null;
  }
}
